import { z } from 'zod'

import { coursePath } from '../courses/paths'

/** The kinds of activity a course holds; later items add theirs to the registry (`activity-types.ts`). */
export const activityTypes = ['section', 'html', 'externalLink', 'document', 'video', 'lab', 'scorm', 'cmi5', 'ltiLink', 'assessment'] as const

export type ActivityType = (typeof activityTypes)[number]

export const availabilities = ['everyone', 'instructorsOnly'] as const

export type Availability = (typeof availabilities)[number]

const labSchema = z.object({
  labProfileId: z.number().int(),
  instructionSetId: z.string().nullish(),
  instructionSetLanguage: z.string().nullish(),
  allowsMultipleAttempts: z.boolean(),
  allowsMarkComplete: z.boolean(),
  savedLabDaysForClassEnrollments: z.number().int().nullish(),
  savedLabDaysForCourseAssignments: z.number().int().nullish(),
  savedLabDaysForInstructors: z.number().int().nullish(),
  savedLabDaysForClassSessionGaps: z.number().int().nullish(),
})

/** A lab activity's own settings; a saved-lab day limit that is empty takes the organization's default. */
export type LabSettings = z.infer<typeof labSchema>

const scormSchema = z.object({ contentPackageId: z.string() })

/** A SCORM activity's own settings: the SCORM module it plays. */
export type ScormSettings = z.infer<typeof scormSchema>

const cmi5Schema = z.object({ contentPackageId: z.string(), assignableUnitId: z.string() })

/** A cmi5 activity's own settings: the cmi5 package and the assignable unit it launches. */
export type Cmi5Settings = z.infer<typeof cmi5Schema>

const ltiLinkSchema = z.object({
  toolId: z.string().nullish(),
  targetLinkUri: z.string().nullish(),
  customParameters: z.record(z.string(), z.string()),
  requiresScoreForCompletion: z.boolean(),
})

/** An LTI link activity's own settings: its tool in Launch (`ltt_…`), none until one is chosen. */
export type LtiLinkSettings = z.infer<typeof ltiLinkSchema>

const assessmentSchema = z.object({
  assessmentId: z.string(),
  allowsMultipleAttempts: z.boolean(),
  allowsReview: z.boolean(),
})

/** An assessment activity's own settings: the assessment taken (`asm_…`). */
export type AssessmentSettings = z.infer<typeof assessmentSchema>

interface ActivityShape {
  readonly id: string
  readonly type: ActivityType
  readonly name: string
  readonly subtitle?: string | null | undefined
  readonly description?: string | null | undefined
  readonly isRequired: boolean
  readonly enforcesSequence: boolean
  readonly availableInstructorLed: boolean
  readonly availableSelfPaced: boolean
  readonly availability: Availability
  readonly expectedDurationMinutes: number
  readonly opensInNewWindow: boolean
  readonly assignedToStudentsManually: boolean
  readonly attemptLimit?: number | null | undefined
  readonly url?: string | null | undefined
  readonly htmlContent?: string | null | undefined
  readonly lab?: LabSettings | null | undefined
  readonly scorm?: ScormSettings | null | undefined
  readonly cmi5?: Cmi5Settings | null | undefined
  readonly ltiLink?: LtiLinkSettings | null | undefined
  readonly assessment?: AssessmentSettings | null | undefined
  readonly children: readonly ActivityShape[]
}

const activitySchema: z.ZodType<ActivityShape> = z.lazy(() =>
  z.object({
    id: z.string(),
    type: z.enum(activityTypes),
    name: z.string(),
    subtitle: z.string().nullish(),
    description: z.string().nullish(),
    isRequired: z.boolean(),
    enforcesSequence: z.boolean(),
    availableInstructorLed: z.boolean(),
    availableSelfPaced: z.boolean(),
    availability: z.enum(availabilities),
    expectedDurationMinutes: z.number(),
    opensInNewWindow: z.boolean(),
    assignedToStudentsManually: z.boolean(),
    attemptLimit: z.number().nullish(),
    url: z.string().nullish(),
    htmlContent: z.string().nullish(),
    lab: labSchema.nullish(),
    scorm: scormSchema.nullish(),
    cmi5: cmi5Schema.nullish(),
    ltiLink: ltiLinkSchema.nullish(),
    assessment: assessmentSchema.nullish(),
    children: z.array(activitySchema),
  }),
)

/** One activity of a course, a section with its own. */
export type Activity = ActivityShape

const treeSchema = z.object({
  courseId: z.string(),
  courseName: z.string(),
  activities: z.array(activitySchema),
  /** Whether a class or course assignment has delivered the course, so adding or removing labs needs `labs-after-delivery`. */
  isDelivered: z.boolean().optional(),
})

/** A course's activities, as the caller may see them. */
export type ActivityTree = z.infer<typeof treeSchema>

export function parseActivityTree(body: unknown): ActivityTree {
  return treeSchema.parse(body)
}

const copiedIdsSchema = z.object({ activityIds: z.record(z.string(), z.string()).nullish() })

/** From a course copy's answer: each activity of the course copied, by id, to its id in the copy; `undefined` when it names none. */
export function parseCopiedActivityIds(body: unknown): ReadonlyMap<string, string> | undefined {
  const { activityIds } = copiedIdsSchema.parse(body)
  return activityIds === null || activityIds === undefined ? undefined : new Map(Object.entries(activityIds))
}

const detailsSchema = z.object({ activity: activitySchema, courseId: z.string(), courseName: z.string(), sectionName: z.string().nullish() })

/** One activity, with its course and section, as Activity Details shows it. */
export type ActivityDetails = z.infer<typeof detailsSchema>

export function parseActivityDetails(body: unknown): ActivityDetails {
  return detailsSchema.parse(body)
}

/** The API's activity endpoints of a course. */
export function courseActivitiesApi(courseId: string): string {
  return `/catalog/v1/courses/${encodeURIComponent(courseId)}/activities`
}

export function courseActivityApi(courseId: string, activityId: string): string {
  return `${courseActivitiesApi(courseId)}/${encodeURIComponent(activityId)}`
}

/** Activity Details. */
export const activityRoute = '/catalog/courses/:publicId/activities/:activityId'

export function activityPath(courseId: string, activityId: string): string {
  return `${coursePath(courseId)}/activities/${encodeURIComponent(activityId)}`
}

/** Seeing a course's instructors-only activities. */
export const readInstructorContentPermission = 'catalog.courses.instructor-content.read'

/** Changing a lab activity's saved-lab storage policies. */
export const writeSavedLabPoliciesPermission = 'catalog.courses.saved-lab-policies.write'

/**
 * The slots the activity pages offer other areas, each handed the activity's id as `recordId`: an
 * activity's editor (`activityEditorSlot`), and on each Activities tab row and on Activity Details,
 * panels under it, such as Credentials' skills (`activityPanelsSlot`), and actions beside it, such
 * as Launch's (`activityActionsSlot`).
 */
export const activityEditorSlot = 'catalog.activity-editor'
export const activityPanelsSlot = 'catalog.activity.panels'
export const activityActionsSlot = 'catalog.activity.actions'

/** "2h 05m" from minutes. */
export function durationParts(minutes: number): { readonly hours: number; readonly minutes: string } {
  return { hours: Math.floor(minutes / 60), minutes: String(minutes % 60).padStart(2, '0') }
}
